import usePracticeExchange from "features/onboarding/guided/usePracticeExchange";
import {
  useOnboardingAnalytics,
  useOnboardingStepView,
} from "analytics/onboarding";
import useOnboardingPracticeAccount from "features/onboarding/guided/useOnboardingPracticeAccount";
import { ACCOUNT_ID_QUERY_PARAM } from "features/bots/common/constants";
import { useContext } from "react";
import { useSelector } from "react-redux";
import { NextArrow } from "features/onboarding/common/OnboardingIcons";
import Button from "common/Buttons/Button";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces, WorkspaceType } from "@shared/types";
import { selectWorkspace } from "@shared/slices/common.slice";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import { OnboardingDetailedStep } from "@shared/features/onboarding/types";
import { TradingIntent } from "features/onboarding/guided/intent";
import {
  ONBOARDING_PREVIEW_BOT_ID,
  ONBOARDING_INTENT_CONTINUE_ID,
  ONBOARDING_WALKTHROUGH_BACK_ID,
} from "testIds";
import useStartLive from "features/onboarding/guided/useStartLive";
import WalkthroughLayout from "features/onboarding/guided/walkthrough/WalkthroughLayout";
import {
  WalkthroughAlternatives,
  WalkthroughLiveRow,
} from "features/onboarding/guided/walkthrough/WalkthroughActions";
import WalkthroughMeta from "features/onboarding/guided/walkthrough/WalkthroughMeta";
import WalkthroughPanel from "features/onboarding/guided/walkthrough/WalkthroughPanel";
import WalkthroughSteps from "features/onboarding/guided/walkthrough/WalkthroughSteps";
import {
  SCENE_STEPS,
  WALKTHROUGH_STEPS,
} from "features/onboarding/guided/walkthrough/steps";
import useSceneWalkthrough from "features/onboarding/guided/walkthrough/useSceneWalkthrough";
import { VISUAL_EVENTS } from "features/onboarding/visuals/types";
import { hasBotVisual } from "features/onboarding/visuals/BotVisual";

const MethodWalkthrough = ({ intent }: { intent: TradingIntent }) => {
  useOnboardingStepView("method_walkthrough");
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  // The bot illustrations step through their own events.
  const scene = hasBotVisual(intent) ? intent : null;
  const totalEvents = scene
    ? VISUAL_EVENTS[scene]
    : intent === "signals"
    ? 4
    : 3;
  const walk = useSceneWalkthrough(scene, totalEvents);
  const { stage, chartRef, advance, playing, togglePlayback, goTo } = walk;
  const startLive = useStartLive();
  const demo = useSelector(selectWorkspace) === WorkspaceType.DEMO;
  const { exchange } = usePracticeExchange();
  const { account: practiceAccount } = useOnboardingPracticeAccount(exchange);
  const { changeStep, finishOnboardingLoading } =
    useContext(OnboardingContext)!;
  const track = useOnboardingAnalytics();
  const continueSetup = (prepared = false): void => {
    track(
      "Walkthrough Choice",
      demo
        ? {
            choice: prepared
              ? "preview_bot"
              : intent === "grid"
              ? "build_own"
              : "create_demo",
            // Signals and DCA go on to a list to pick from; the others to a
            // form (prepared or their own).
            onboarding_path: prepared
              ? "demo_prepared"
              : intent === "signals" || intent === "dca"
              ? "demo_browse"
              : "demo_build_own",
          }
        : { choice: "continue_live" }
    );
    if (demo && !practiceAccount) {
      changeStep(OnboardingDetailedStep.CONNECT_EXCHANGE, {
        guided: "1",
        prepareDemo: "1",
        setup: "1",
        preset: prepared && intent === "grid" ? "grid" : "",
        exchange,
        "account-id": "",
        botUUID: "",
      });
      return;
    }
    if (intent === "signals" || intent === "dca") {
      changeStep(
        intent === "signals"
          ? OnboardingDetailedStep.FOLLOW_SIGNALS_GROUP
          : OnboardingDetailedStep.COPY_DCA_BOT,
        {
          guided: "0",
          method: intent,
          ...(demo && practiceAccount
            ? { [ACCOUNT_ID_QUERY_PARAM]: String(practiceAccount.client_id) }
            : {}),
        }
      );
    } else
      changeStep(OnboardingDetailedStep.TRADING_METHOD, {
        setup: "1",
        preset: prepared && demo && intent === "grid" ? "grid" : "",
        botUUID: "",
        ...(practiceAccount
          ? { [ACCOUNT_ID_QUERY_PARAM]: practiceAccount.client_id.toString() }
          : {}),
      });
  };
  const actionLabel =
    stage === totalEvents
      ? t("intent.replay")
      : scene
      ? t("intent.next_event")
      : stage === 0
      ? t(`intent.${intent}.action`)
      : stage === 3
      ? t("revision.signals_next3")
      : t(`revision.${intent}_next${stage as 1 | 2}`);
  const back = () => {
    track("Back Clicked", { from_step: "method_walkthrough" });
    changeStep(OnboardingDetailedStep.SELECT_EXCHANGE, {
      startMode: "",
      connectLive: "",
      guided: "0",
    });
  };
  return (
    <WalkthroughLayout
      title={t(`intent.${intent}.name`)}
      subtitle={t("intent.walkthrough_description")}
      meta={<WalkthroughMeta demo={demo} />}
      story={
        <WalkthroughSteps
          steps={
            walk.steps ??
            WALKTHROUGH_STEPS[intent].map((key) => ({ title: t(key) }))
          }
          progressRef={walk.barRef}
          stage={stage}
          onSelect={goTo}
        />
      }
      panel={
        <WalkthroughPanel
          intent={intent}
          stage={stage}
          total={totalEvents}
          label={
            scene
              ? t(`visual.${scene}.label`)
              : intent === "signals"
              ? t(`revision.signals_visual${stage as 0 | 1 | 2 | 3 | 4}`)
              : t(`revision.${intent}_visual${stage as 0 | 1 | 2 | 3}`)
          }
          caption={
            stage === 0
              ? t("intent.waiting")
              : scene
              ? t(SCENE_STEPS[scene][stage - 1].description)
              : intent === "signals"
              ? t(`revision.signals_caption${stage as 1 | 2 | 3 | 4}`)
              : t(`intent.${intent}.result${stage as 1 | 2 | 3}`)
          }
          playing={playing}
          {...walk.panel}
          togglePlayback={togglePlayback}
          advance={advance}
          actionLabel={actionLabel}
          chartRef={chartRef}
        />
      }
      actions={
        demo ? (
          <>
            <Button
              type="primary"
              size="large"
              loading={intent === "grid" ? undefined : finishOnboardingLoading}
              onClick={() => continueSetup(intent === "grid")}
              data-testid={
                intent === "grid"
                  ? ONBOARDING_PREVIEW_BOT_ID
                  : ONBOARDING_INTENT_CONTINUE_ID
              }
            >
              {t(
                intent === "grid"
                  ? "guided.preview_bot"
                  : "walkthrough.create_demo"
              )}{" "}
              <NextArrow />
            </Button>
            {intent === "grid" && (
              <WalkthroughAlternatives
                routes={[
                  {
                    label: t("walkthrough.build_own_demo"),
                    onClick: () => continueSetup(),
                    testId: ONBOARDING_INTENT_CONTINUE_ID,
                    loading: finishOnboardingLoading,
                  },
                ]}
              />
            )}
          </>
        ) : (
          <>
            <Button
              type="primary"
              size="large"
              loading={finishOnboardingLoading}
              onClick={() => continueSetup()}
              data-testid={ONBOARDING_INTENT_CONTINUE_ID}
            >
              {t("intent.continue_live")} <NextArrow />
            </Button>
            <WalkthroughAlternatives
              lead={false}
              routes={[
                {
                  label: t("guided.back_choices"),
                  onClick: back,
                  testId: ONBOARDING_WALKTHROUGH_BACK_ID,
                },
              ]}
            />
          </>
        )
      }
      footer={demo ? <WalkthroughLiveRow onClick={startLive} /> : undefined}
    />
  );
};
export default MethodWalkthrough;
